import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumQcProbeProvider } from "./enums/enumQcProbeProvider.generated";

// Model Objects

export class QcProbeProviderStatus extends ModelObject<QcProbeProviderStatus> {

    public isRegistered: boolean = false;
    /**
    * Any error messages associated with communicating with this provider
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public errorMessages: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.isRegistered = jsonSerializedObject.isRegistered;
          this.errorMessages = jsonSerializedObject.errorMessages;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.isRegistered = this.isRegistered;
        toRet.errorMessages = this.errorMessages;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class QcProbe extends ModelObject<QcProbe> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public name: string = "";
    private _vesselId: Guid | null = null;
    /**
    * The vessel this probe is taking measurements from
    */
    public get vesselId(): Guid | null { return this._vesselId }
    public set vesselId(value: Guid | null) {
      this._vesselId = value === undefined ? null : value;
    }
    /**
    * The provider specific identifier for this probe
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public probeIdentifier: string = "";
    /**
    * The manufacturer/provider of the probe
    */
    public provider: EnumQcProbeProvider = EnumQcProbeProvider.none;
    /**
    * Any informational messages associated with the last communication with this probe
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public logMessages: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.vesselId = jsonSerializedObject.vesselId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.vesselId);
          this.probeIdentifier = jsonSerializedObject.probeIdentifier;
          this.provider = EnumQcProbeProvider.getByValue(jsonSerializedObject.provider, this.provider);
          this.logMessages = jsonSerializedObject.logMessages;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.vesselId = this.vesselId == null ? null : this.vesselId.toString();
        toRet.probeIdentifier = this.probeIdentifier;
        toRet.provider = this.provider.value;
        toRet.logMessages = this.logMessages;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "QcProbe.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "vesselId", rule: (v: any) => v == null || !v.isEmpty() || "QcProbe.VesselId cannot be empty"},
    ];
}


export class FilteredQcProbesResult extends ModelObject<FilteredQcProbesResult> {

    public matchingCount: number = 0;
    public qcProbes: (QcProbe)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.qcProbes = jsonSerializedObject.qcProbes.map((v: any) => new QcProbe(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.qcProbes = this.qcProbes.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "qcProbes", rule: (v: any) => !!v || "qcProbes is required"},
            {field: "qcProbes", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as QcProbe[]).reduce((acc: Array<string>, curr: QcProbe) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class QcProbeFilter extends ModelObject<QcProbeFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _providers: readonly (EnumQcProbeProvider)[] | null = null;
    /**
    * Limit to certain providers
    */
    public get providers(): readonly (EnumQcProbeProvider)[] | null { return this._providers }
    public set providers(value: readonly (EnumQcProbeProvider)[] | null) {
      this._providers = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.providers = jsonSerializedObject.providers == null ? null : jsonSerializedObject.providers.map((v: any) => EnumQcProbeProvider.getByValue(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.providers = this.providers == null ? null : this.providers.map(v => v.value);
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class QcProbeService extends ServiceBase {

  public async getAllQcProbes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(QcProbe)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.qcProbes.map((v: any) => new QcProbe(v));;
    return this.makeJsonRequest("qcProbe/v1/getAllQcProbes", req, false, extractor, opts);
  };

  public async getQcProbesByFilter(filter: QcProbeFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredQcProbesResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredQcProbesResult(response.results);;
    return this.makeJsonRequest("qcProbe/v1/getQcProbesByFilter", req, false, extractor, opts);
  };

  public async updateQcProbe(qcProbeId: Guid, version: number, vesselId: Guid | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    qcProbeId:qcProbeId.toString(),
    version:version,
    vesselId:vesselId == null ? null : vesselId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("qcProbe/v1/updateQcProbe", req, true, extractor, opts);
  };

  public async registerPlaatoIntegration(apiToken: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    apiToken:apiToken,
    };
    const extractor = null;
    return this.makeJsonRequest("qcProbe/v1/registerPlaatoIntegration", req, true, extractor, opts);
  };

  public async unregisterPlaatoIntegration(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("qcProbe/v1/unregisterPlaatoIntegration", req, true, extractor, opts);
  };

  public async refreshPlaatoIntegration(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("qcProbe/v1/refreshPlaatoIntegration", req, true, extractor, opts);
  };

  public async getPlaatoIntegrationStatus(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<QcProbeProviderStatus> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new QcProbeProviderStatus(response.status);;
    return this.makeJsonRequest("qcProbe/v1/getPlaatoIntegrationStatus", req, false, extractor, opts);
  };
}

export const qcProbeService = new QcProbeService();